import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { delay } from "@faro-lotv/foundation";
import { Matrix4, Quaternion, Vector3Tuple, Vector4Tuple } from "three";

/** Points of a point cloud, in world coordinates relative to an origin to keep the float precision */
export type FloorSample = {
  /** World position the points are relative to */
  origin: Vector3Tuple;

  /** Positions (x, y, z) relative to the origin */
  positions: Float32Array;

  /** Index of the scan of each point, if the point cloud knows it */
  scanIds?: Uint16Array;
};

/** A scanner position, the floor of a level is below it */
export type FloorScanner = {
  /** World position of the scanner */
  position: Vector3Tuple;

  /** Index of the scan in the point cloud scanIds, if known */
  scanIndex?: number;
};

/** The cells of a floor counted in its area, on a grid in the X/Z plane of the segmentation frame */
export type FloorFootprint = {
  /** Size of a cell in meters */
  cellSize: number;

  /** Number of cells along X */
  cols: number;

  /** Number of cells along Z */
  rows: number;

  /** Frame coordinates of the corner of the first cell */
  minX: number;
  minZ: number;

  /** 1 for the counted cells, indexed as row * cols + col */
  mask: Uint8Array;
};

/** A floor detected in the point cloud */
export type IpxFloor = {
  /** World height of the floor surface */
  elevation: number;

  /** Area of the floor in square meters, walls included */
  area: number;

  /** World heights of the points of the floor surface, from the lowest to the highest scanner floor */
  band: [number, number];

  /** The counted area */
  footprint: FloorFootprint;
};

/** The floors detected in a region */
export type FloorSegmentation = {
  /** World pose of the frame of the footprints: the region box, rotated around Y only */
  frame: { position: Vector3Tuple; rotation: Vector4Tuple };

  /** The floors, from the lowest */
  floors: IpxFloor[];

  /** Number of sample points in the region and of the visible scans */
  pointsInRegion: number;
};

export type FloorSegmentationInput = {
  /** The points to analyse */
  sample: FloorSample;

  /** The region to analyse, the whole sample if undefined */
  box?: OrientedBoundingBox;

  /** Indices of the scans to ignore */
  hiddenScans?: ReadonlySet<number>;

  /** The scanner positions, used to find the floors */
  scanners: FloorScanner[];
};

/** Longest stretch of analysis before the view can render a frame, in milliseconds: shorter slices spend more time rendering */
const SLICE_MS = 40;
/** Points processed between two checks of the slice time */
const POINTS_PER_CHECK = 65_536;
/** Height of the histogram bins */
const BIN_SIZE = 0.05;
/** Range of the scanner height above its floor, below 0.9 m there are table tops */
const SCANNER_HEIGHT = { min: 0.9, max: 2.2 };
/** The scanners of a project stand at about the same height above their floor, within this tolerance */
const SCANNER_HEIGHT_TOLERANCE = 0.1;
/** Scanners not at the usual height are on stairs or ramps, it takes this many to make a level */
const MIN_OFF_HEIGHT_SCANNERS = 2;
/** A surface below a scanner is the densest one within this many histogram bins */
const PEAK_RADIUS_BINS = 3;
/** Points this high above a scanner are its ceiling */
const CEILING_CLEARANCE = 0.3;
/** Horizontal radius around a scanner where its floor is searched */
const SCANNER_FLOOR_RADIUS = 3;
/** Fewer floor points than this around a scanner and its floor is ignored */
const MIN_SCANNER_FLOOR_POINTS = 30;
/** Floors of scanners closer than this are the same level, higher steps are levels of their own */
const SAME_LEVEL_DISTANCE = 0.15;
/** Horizontal radius around a scanner where its ceiling is searched */
const SCANNER_CEILING_RADIUS = 1.5;
/** A scanner is outside the building when there are fewer points above it than this, relative to its floor */
const MIN_CEILING_RATIO = 0.15;
/** A box top lower than this above a scanner floor may cut its ceiling, the scanner then counts as inside */
const CUT_CEILING_HEIGHT = 3;
/** Points this high above a floor cover it: ceilings, upper floors, roofs. Outside the building nothing does */
const COVER_CLEARANCE = 1.8;
/** Below this ratio of covered floor cells, the box cuts the ceilings and the cover is not checked */
const MIN_COVERED_RATIO = 0.2;
/** Floors closer in height than this cannot overlap in plan, e.g. a raised part next to the main floor */
const MIN_HEADROOM = 2.2;
/** A floor cell has no points this high above the floor, unlike a wall at the same height */
const FREE_HEADROOM = { min: 0.3, max: 1 };
/** Without scanners, histogram peaks closer than this are the same surface */
const PEAK_SEPARATION = 0.3;
/** Half thickness of the floor surface */
const FLOOR_TOLERANCE = 0.1;
/** Heights above the floor where the walls are rasterised */
const WALL_RANGE = { min: 0.3, max: 2 };
/** Default cell size of the footprint grid */
const BASE_CELL_SIZE = 0.1;
/** The cell size grows to keep the grid below this many cells */
const MAX_CELLS = 2_000_000;
/** The cell size grows to keep the footprint textures within 4096 px, the padding included */
const MAX_GRID_SIDE = 4000;
/** Gaps narrower than twice this are closed, e.g. doors and occlusions */
const CLOSING_RADIUS = 0.5;
/** The scans only see the inner face of the outer walls, add their thickness */
const OUTER_WALL_ALLOWANCE = 0.2;
/** Enclosed unscanned areas are filled if mostly under a ceiling, open courtyards are not */
const MIN_COVERED_HOLE_SHARE = 0.5;
/** Surfaces less dense than this relative to the densest one are ignored */
const MIN_SURFACE_DENSITY = 0.15;
/** Without scanners, a floor lies this far above the ceiling below it, the slab thickness */
const SLAB_THICKNESS = { min: 0.05, max: 0.8 };
/** Without scanners, floors are at least this far apart */
const MIN_STOREY_HEIGHT = 2.2;
/** Smaller floors are dropped, e.g. stair landings */
const MIN_FLOOR_AREA = 4;
/** Without scanners, smaller footprint parts are dropped as noise */
const MIN_PART_AREA = 2;

/**
 * Splits the points of a region in floors and estimates the area of each floor, walls included.
 * The estimate errs on the high side: gaps are closed, holes filled and the outer walls thickness added.
 * The analysis pauses regularly, so the view keeps rendering.
 *
 * @param input The points, the region and the scanners
 * @param signal Stops the analysis, e.g. when the region changes
 * @returns the floors of the region
 */
export async function segmentFloors(
  input: FloorSegmentationInput,
  signal?: AbortSignal,
): Promise<FloorSegmentation> {
  const pause = createPause(signal);
  const frame = computeFrame(input);
  const local = await toFrame(input, frame, pause);
  const [hx, , hz] = local.bounds ?? [Infinity, Infinity, Infinity];
  const scanners = input.scanners
    .map((scanner) => ({
      ...scanner,
      local: toFrameCoords(scanner.position, frame),
    }))
    .filter(({ local: [x, , z] }) => Math.abs(x) <= hx && Math.abs(z) <= hz);

  const pointsInRegion = local.count;
  if (local.count === 0) {
    return { frame: toTuples(frame), floors: [], pointsInRegion };
  }

  const scannerFloors = await findScannerFloors(local, scanners, pause);
  const inside = scannerFloors.filter((floor) => floor.isInside);
  // No ceiling above any scanner: the box cuts the ceilings, keep them all
  const kept = inside.length ? inside : scannerFloors;
  if (inside.length) {
    const outside = scannerFloors.flatMap((f) =>
      f.isInside ? [] : (f.scanIndex ?? []),
    );
    dropScans(local, new Set(outside));
  }

  let levels = groupLevels(kept);
  if (!levels.length) levels = findLevelsFromHistogram(local);

  const grid = computeGrid(local);
  const floors: IpxFloor[] = [];
  for (const level of levels) {
    const others = levels.filter((other) => other !== level);
    const footprint = await computeFootprint(local, grid, level, others, pause);
    const area = countCells(footprint.mask) * footprint.cellSize ** 2;
    if (area < MIN_FLOOR_AREA) continue;
    const y = frame.position[1];
    floors.push({
      elevation: y + level.elevation,
      area,
      band: [y + level.band[0], y + level.band[1]],
      footprint: cropFootprint(footprint),
    });
  }
  return { frame: toTuples(frame), floors, pointsInRegion };
}

/** Waits for the next frame once the analysis ran for a slice, throws once aborted */
type Pause = () => Promise<void>;

/**
 * @param signal Stops the analysis
 * @returns the pause of an analysis
 */
function createPause(signal?: AbortSignal): Pause {
  let sliceStart = performance.now();
  return () => {
    signal?.throwIfAborted();
    if (performance.now() - sliceStart < SLICE_MS) return Promise.resolve();
    return delay(0).then(() => {
      signal?.throwIfAborted();
      sliceStart = performance.now();
    });
  };
}

type Frame = { position: Vector3Tuple; rotation: Quaternion };

/** The points in the frame coordinates, Y up */
type LocalPoints = {
  x: Float32Array;
  y: Float32Array;
  z: Float32Array;
  scanIds?: Uint16Array;
  count: number;
  /** Half size of the region box, undefined without a box */
  bounds?: Vector3Tuple;
};

type Level = {
  /** Frame height of the floor */
  elevation: number;
  /** Frame heights of the floor points */
  band: [number, number];
  /** Frame X/Z of the scanners of this level */
  scanners: Array<[number, number]>;
};

/** The floor found below a scanner */
type ScannerFloor = {
  /** Frame height of the floor */
  elevation: number;
  /** Frame X/Z of the scanner */
  scanner: [number, number];
  /** Index of the scan, if known */
  scanIndex?: number;
  /** False if nothing covers the scanner, it is outside the building */
  isInside: boolean;
  /** False if the scanner is not at the usual height above its floor, e.g. on stairs */
  isUsualHeight: boolean;
};

/**
 * @param input The segmentation input
 * @returns the frame of the segmentation: the box center and rotation, or the sample origin
 */
function computeFrame({ box, sample }: FloorSegmentationInput): Frame {
  return box
    ? {
        position: box.position,
        rotation: new Quaternion().fromArray(box.rotation),
      }
    : { position: sample.origin, rotation: new Quaternion() };
}

/**
 * @param frame A frame
 * @returns the frame as plain tuples
 */
function toTuples(frame: Frame): FloorSegmentation["frame"] {
  return { position: frame.position, rotation: frame.rotation.toArray() };
}

/**
 * @param position A world position
 * @param frame The frame
 * @returns the position in the frame coordinates
 */
function toFrameCoords(position: Vector3Tuple, frame: Frame): Vector3Tuple {
  const inv = new Matrix4().makeRotationFromQuaternion(
    frame.rotation.clone().invert(),
  ).elements;
  const dx = position[0] - frame.position[0];
  const dy = position[1] - frame.position[1];
  const dz = position[2] - frame.position[2];
  return [
    inv[0] * dx + inv[4] * dy + inv[8] * dz,
    inv[1] * dx + inv[5] * dy + inv[9] * dz,
    inv[2] * dx + inv[6] * dy + inv[10] * dz,
  ];
}

/**
 * @param input The segmentation input
 * @param frame The frame of the segmentation
 * @param pause Pauses the analysis
 * @returns the points inside the box and of the visible scans, in the frame coordinates
 */
async function toFrame(
  input: FloorSegmentationInput,
  frame: Frame,
  pause: Pause,
): Promise<LocalPoints> {
  const { sample, box, hiddenScans } = input;
  const { positions, scanIds } = sample;
  const total = positions.length / 3;
  const inv = new Matrix4().makeRotationFromQuaternion(
    frame.rotation.clone().invert(),
  ).elements;
  const ox = sample.origin[0] - frame.position[0];
  const oy = sample.origin[1] - frame.position[1];
  const oz = sample.origin[2] - frame.position[2];
  const bounds: Vector3Tuple | undefined = box
    ? [box.size[0] / 2, box.size[1] / 2, box.size[2] / 2]
    : undefined;
  const [hx, hy, hz] = bounds ?? [Infinity, Infinity, Infinity];

  const x = new Float32Array(total);
  const y = new Float32Array(total);
  const z = new Float32Array(total);
  const ids = scanIds ? new Uint16Array(total) : undefined;
  let count = 0;
  for (let i = 0; i < total; i++) {
    if (i % POINTS_PER_CHECK === 0) await pause();
    if (scanIds && hiddenScans?.has(scanIds[i])) continue;
    const dx = positions[i * 3] + ox;
    const dy = positions[i * 3 + 1] + oy;
    const dz = positions[i * 3 + 2] + oz;
    const lx = inv[0] * dx + inv[4] * dy + inv[8] * dz;
    const ly = inv[1] * dx + inv[5] * dy + inv[9] * dz;
    const lz = inv[2] * dx + inv[6] * dy + inv[10] * dz;
    if (Math.abs(lx) > hx || Math.abs(ly) > hy || Math.abs(lz) > hz) continue;
    x[count] = lx;
    y[count] = ly;
    z[count] = lz;
    if (ids && scanIds) ids[count] = scanIds[i];
    count++;
  }
  return { x, y, z, scanIds: ids, count, bounds };
}

/**
 * Removes, in place, the points of some scans
 *
 * @param local The points
 * @param scans Indices of the scans to remove
 */
function dropScans(local: LocalPoints, scans: ReadonlySet<number>): void {
  const { scanIds } = local;
  if (!scanIds || !scans.size) return;
  let count = 0;
  for (let i = 0; i < local.count; i++) {
    if (scans.has(scanIds[i])) continue;
    local.x[count] = local.x[i];
    local.y[count] = local.y[i];
    local.z[count] = local.z[i];
    scanIds[count] = scanIds[i];
    count++;
  }
  local.count = count;
}

/**
 * @param local The points
 * @returns the min and max height of the points
 */
function heightRange(local: LocalPoints): [number, number] {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < local.count; i++) {
    min = Math.min(min, local.y[i]);
    max = Math.max(max, local.y[i]);
  }
  return [min, max];
}

/**
 * @param histogram A histogram
 * @returns the histogram smoothed with a 3 bins box filter
 */
function smooth(histogram: Float32Array): Float32Array {
  const out = new Float32Array(histogram.length);
  for (let i = 0; i < histogram.length; i++) {
    out[i] =
      ((histogram[i - 1] ?? 0) + histogram[i] + (histogram[i + 1] ?? 0)) / 3;
  }
  return out;
}

/**
 * @param smoothed A smoothed histogram
 * @param radius Radius of the neighbourhood of a peak, in bins
 * @returns the bins of the peaks denser than MIN_SURFACE_DENSITY of the densest one
 */
function findPeaks(smoothed: Float32Array, radius: number): number[] {
  const densest = Math.max(...smoothed);
  const peaks: number[] = [];
  for (let b = 0; b < smoothed.length; b++) {
    if (!smoothed[b] || smoothed[b] < MIN_SURFACE_DENSITY * densest) continue;
    let isPeak = true;
    for (
      let n = Math.max(0, b - radius);
      n <= Math.min(smoothed.length - 1, b + radius);
      n++
    ) {
      // Ties keep the lowest bin, so a flat top gives one peak
      if (smoothed[n] > smoothed[b] || (smoothed[n] === smoothed[b] && n < b)) {
        isPeak = false;
      }
    }
    if (isPeak) peaks.push(b);
  }
  return peaks;
}

/**
 * The floor of a scanner is the surface below it at the usual scanner height of the project:
 * the densest or the highest surface can be a table, or the ceiling of the level below.
 * A scanner with almost nothing above it is outside the building.
 *
 * @param local The points
 * @param scanners The scanners in the region, with their frame coordinates
 * @param pause Pauses the analysis
 * @returns the floors below the scanners
 */
async function findScannerFloors(
  local: LocalPoints,
  scanners: Array<FloorScanner & { local: Vector3Tuple }>,
  pause: Pause,
): Promise<ScannerFloor[]> {
  if (!scanners.length) return [];
  const bins = Math.ceil((SCANNER_HEIGHT.max - SCANNER_HEIGHT.min) / BIN_SIZE);
  // Histograms of the scanner height above the points around it
  const histograms = scanners.map(() => new Float32Array(bins));
  // Points near the scanner axis, below in the floor range and above it
  const below = new Float32Array(scanners.length);
  const above = new Float32Array(scanners.length);
  const radius2 = SCANNER_FLOOR_RADIUS ** 2;
  const ceilingRadius2 = SCANNER_CEILING_RADIUS ** 2;

  function addPoint(k: number, i: number): void {
    const [sx, sy, sz] = scanners[k].local;
    const d2 = (local.x[i] - sx) ** 2 + (local.z[i] - sz) ** 2;
    if (d2 > radius2) return;
    const isNear = d2 <= ceilingRadius2;
    const height = sy - local.y[i];
    if (height < -CEILING_CLEARANCE) {
      if (isNear) above[k]++;
      return;
    }
    const bin = Math.floor((height - SCANNER_HEIGHT.min) / BIN_SIZE);
    if (bin < 0 || bin >= bins) return;
    if (isNear) below[k]++;
    histograms[k][bin]++;
  }

  // A grid of cells of the floor radius around the scanners, each cell lists the scanners in reach of its points
  function toCell(v: number): number {
    return Math.floor(v / SCANNER_FLOOR_RADIUS);
  }
  const cols = scanners.map(({ local: [x] }) => toCell(x));
  const rows = scanners.map(({ local: [, , z] }) => toCell(z));
  const [minCol, minRow] = [Math.min(...cols) - 1, Math.min(...rows) - 1];
  const width = Math.max(...cols) + 2 - minCol;
  const height = Math.max(...rows) + 2 - minRow;
  // Only the cells near the scanners are stored, the scanners can be far apart
  const inReach = new Map<number, number[]>();
  for (let k = 0; k < scanners.length; k++) {
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        const cell = (rows[k] + dr - minRow) * width + cols[k] + dc - minCol;
        const inCell = inReach.get(cell);
        if (inCell) inCell.push(k);
        else inReach.set(cell, [k]);
      }
    }
  }
  for (let i = 0; i < local.count; i++) {
    if (i % POINTS_PER_CHECK === 0) await pause();
    const col = toCell(local.x[i]) - minCol;
    const row = toCell(local.z[i]) - minRow;
    if (col < 0 || col >= width || row < 0 || row >= height) continue;
    for (const k of inReach.get(row * width + col) ?? []) addPoint(k, i);
  }

  // The heights of each scanner above the surfaces below it
  const candidates = histograms.map((histogram) =>
    histogram.reduce((a, v) => a + v, 0) < MIN_SCANNER_FLOOR_POINTS
      ? []
      : findPeaks(smooth(histogram), PEAK_RADIUS_BINS).map((bin) =>
          refinePeak(histogram, bin, SCANNER_HEIGHT.min),
        ),
  );
  const usual = findUsualHeight(candidates);
  return candidates.flatMap((heights, k) => {
    if (!heights.length) return [];
    const height = heights.reduce((a, b) =>
      Math.abs(b - usual) < Math.abs(a - usual) ? b : a,
    );
    const [x, y, z] = scanners[k].local;
    const elevation = y - height;
    const isCeilingCut =
      local.bounds !== undefined &&
      local.bounds[1] - elevation < CUT_CEILING_HEIGHT;
    return {
      elevation,
      scanner: [x, z],
      scanIndex: scanners[k].scanIndex,
      isInside: isCeilingCut || above[k] >= MIN_CEILING_RATIO * below[k],
      isUsualHeight: Math.abs(height - usual) <= SCANNER_HEIGHT_TOLERANCE,
    };
  });
}

/**
 * @param candidates For each scanner, its heights above the surfaces below it
 * @returns the height shared by the most scanners
 */
function findUsualHeight(candidates: number[][]): number {
  // A window of the tolerance around each height slides over the sorted heights, counting the scanners in it
  const sorted = candidates
    .flatMap((heights, scanner) =>
      heights.map((height) => ({ height, scanner })),
    )
    .sort((a, b) => a.height - b.height);
  const inWindow = new Uint32Array(candidates.length);
  let scanners = 0;
  let [first, end] = [0, 0];
  let usual = { height: 0, scanners: 0 };
  for (const { height } of sorted) {
    for (
      ;
      end < sorted.length &&
      sorted[end].height <= height + SCANNER_HEIGHT_TOLERANCE;
      end++
    ) {
      if (inWindow[sorted[end].scanner]++ === 0) scanners++;
    }
    for (; sorted[first].height < height - SCANNER_HEIGHT_TOLERANCE; first++) {
      if (--inWindow[sorted[first].scanner] === 0) scanners--;
    }
    if (scanners > usual.scanners) usual = { height, scanners };
  }
  return usual.height;
}

/**
 * @param floors The floors of the scanners
 * @returns the levels, from the lowest: the scanner floors at about the same height, without stairs and ramps
 */
function groupLevels(floors: ScannerFloor[]): Level[] {
  const sorted = [...floors].sort((a, b) => a.elevation - b.elevation);
  const groups: ScannerFloor[][] = [];
  for (const floor of sorted) {
    const last = groups.at(-1);
    const lastFloor = last?.at(-1);
    if (
      last &&
      lastFloor &&
      floor.elevation - lastFloor.elevation <= SAME_LEVEL_DISTANCE
    ) {
      last.push(floor);
    } else {
      groups.push([floor]);
    }
  }
  const levels = groups.filter(
    (group) =>
      group.some((floor) => floor.isUsualHeight) ||
      group.length >= MIN_OFF_HEIGHT_SCANNERS,
  );
  return levels.map((group) => ({
    elevation: group[Math.floor(group.length / 2)].elevation,
    band: [
      group[0].elevation - FLOOR_TOLERANCE,
      (group.at(-1)?.elevation ?? group[0].elevation) + FLOOR_TOLERANCE,
    ],
    scanners: group.map((floor) => floor.scanner),
  }));
}

/**
 * @param histogram A height histogram
 * @param peak The bin of a peak
 * @param minY Height of the first bin
 * @returns the height of the peak, weighted over its neighbour bins
 */
function refinePeak(
  histogram: Float32Array,
  peak: number,
  minY: number,
): number {
  let weight = 0;
  let sum = 0;
  for (
    let b = Math.max(0, peak - 1);
    b <= Math.min(histogram.length - 1, peak + 1);
    b++
  ) {
    weight += histogram[b];
    sum += histogram[b] * (minY + (b + 0.5) * BIN_SIZE);
  }
  return weight > 0 ? sum / weight : minY + (peak + 0.5) * BIN_SIZE;
}

/**
 * Fallback without scanners: the lowest dense surface is a floor,
 * and so is each dense surface lying just above a ceiling, on top of the slab.
 *
 * @param local The points
 * @returns the levels found in the height histogram
 */
function findLevelsFromHistogram(local: LocalPoints): Level[] {
  const [minY, maxY] = heightRange(local);
  const bins = Math.ceil((maxY - minY) / BIN_SIZE) + 1;
  const histogram = new Float32Array(bins);
  for (let i = 0; i < local.count; i++) {
    histogram[Math.floor((local.y[i] - minY) / BIN_SIZE)]++;
  }
  const peaks = findPeaks(
    smooth(histogram),
    Math.round(PEAK_SEPARATION / BIN_SIZE),
  ).map((b) => refinePeak(histogram, b, minY));
  if (!peaks.length) return [];

  const levels = [peaks[0]];
  for (const peak of peaks.slice(1)) {
    const hasCeilingBelow = peaks.some(
      (c) => c < peak - SLAB_THICKNESS.min && c >= peak - SLAB_THICKNESS.max,
    );
    if (peak - (levels.at(-1) ?? 0) >= MIN_STOREY_HEIGHT && hasCeilingBelow) {
      levels.push(peak);
    }
  }
  return levels.map((elevation) => ({
    elevation,
    band: [elevation - FLOOR_TOLERANCE, elevation + FLOOR_TOLERANCE],
    scanners: [],
  }));
}

/** The footprint grid, shared by all the levels */
type Grid = {
  cellSize: number;
  cols: number;
  rows: number;
  minX: number;
  minZ: number;
};

/**
 * @param local The points
 * @returns the footprint grid covering the box, or the points without a box
 */
function computeGrid(local: LocalPoints): Grid {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  if (local.bounds) {
    [minX, maxX, minZ, maxZ] = [
      -local.bounds[0],
      local.bounds[0],
      -local.bounds[2],
      local.bounds[2],
    ];
  } else {
    for (let i = 0; i < local.count; i++) {
      minX = Math.min(minX, local.x[i]);
      maxX = Math.max(maxX, local.x[i]);
      minZ = Math.min(minZ, local.z[i]);
      maxZ = Math.max(maxZ, local.z[i]);
    }
  }
  const area = (maxX - minX) * (maxZ - minZ);
  const cellSize = Math.max(
    BASE_CELL_SIZE,
    Math.sqrt(area / MAX_CELLS),
    Math.max(maxX - minX, maxZ - minZ) / MAX_GRID_SIDE,
  );
  return {
    cellSize,
    // One more cell, so the points on the max edge are in
    cols: Math.floor((maxX - minX) / cellSize) + 1,
    rows: Math.floor((maxZ - minZ) / cellSize) + 1,
    minX,
    minZ,
  };
}

/**
 * Rasterises the floor and the walls of a level, closes the gaps and fills the holes, so the parts
 * where the floor is hidden by clutter count too. Cells with nothing above them are outside the building,
 * and the floors of the levels without headroom to this one are not part of it.
 *
 * @param local The points
 * @param grid The footprint grid
 * @param level The level
 * @param others The other levels, their floor points are not walls of this one
 * @param pause Pauses the analysis
 * @returns the footprint of the level on the grid
 */
async function computeFootprint(
  local: LocalPoints,
  grid: Grid,
  level: Level,
  others: Level[],
  pause: Pause,
): Promise<FloorFootprint> {
  const { cellSize, cols, rows, minX, minZ } = grid;
  const [bandMin, bandMax] = level.band;
  // The grid is padded, so the closing does not erode the parts cut by the box
  const pad =
    Math.ceil(CLOSING_RADIUS / cellSize) +
    Math.ceil(OUTER_WALL_ALLOWANCE / cellSize);
  const w = cols + 2 * pad;
  const h = rows + 2 * pad;
  const floor = new Uint8Array(w * h);
  const walls = new Uint8Array(w * h);
  const cover = new Uint8Array(w * h);
  const nearLevels = others.filter(
    (other) => Math.abs(other.elevation - level.elevation) < MIN_HEADROOM,
  );
  // For each level without headroom to this one, its floor points and the points just above them
  const nearFloors = nearLevels.map(() => new Uint8Array(w * h));
  const nearAbove = nearLevels.map(() => new Uint8Array(w * h));
  for (let i = 0; i < local.count; i++) {
    if (i % POINTS_PER_CHECK === 0) await pause();
    const col = Math.floor((local.x[i] - minX) / cellSize);
    const row = Math.floor((local.z[i] - minZ) / cellSize);
    if (col < 0 || col >= cols || row < 0 || row >= rows) continue;
    const cell = (row + pad) * w + col + pad;
    const y = local.y[i];
    if (y >= bandMax + COVER_CLEARANCE) cover[cell] = 1;
    if (y >= bandMin && y <= bandMax) {
      floor[cell] = 1;
    } else if (y >= bandMin + WALL_RANGE.min && y <= bandMax + WALL_RANGE.max) {
      const isOtherFloor = others.some(
        ({ band }) => y >= band[0] && y <= band[1],
      );
      if (!isOtherFloor) walls[cell] = 1;
    }
    for (const [k, { band }] of nearLevels.entries()) {
      if (y >= band[0] && y <= band[1]) nearFloors[k][cell] = 1;
      if (
        y >= band[1] + FREE_HEADROOM.min &&
        y <= band[1] + FREE_HEADROOM.max
      ) {
        nearAbove[k][cell] = 1;
      }
    }
  }
  const otherFloors = new Uint8Array(w * h);
  for (const [k, cells] of nearFloors.entries()) {
    for (let i = 0; i < cells.length; i++) {
      otherFloors[i] |= cells[i] & (1 - nearAbove[k][i]);
    }
  }

  const closing = Math.ceil(CLOSING_RADIUS / cellSize);
  const covered = dilate(
    cover,
    w,
    h,
    Math.ceil(OUTER_WALL_ALLOWANCE / cellSize),
  );
  let coveredCount = 0;
  for (let i = 0; i < floor.length; i++) coveredCount += floor[i] & covered[i];
  const isCoverChecked = coveredCount >= MIN_COVERED_RATIO * countCells(floor);

  let mask = new Uint8Array(w * h);
  for (let i = 0; i < mask.length; i++) {
    mask[i] = (floor[i] | walls[i]) & (isCoverChecked ? covered[i] : 1);
  }
  await pause();
  mask = erode(dilate(mask, w, h, closing), w, h, closing);
  await pause();
  fillHoles(mask, w, h, isCoverChecked ? covered : undefined);
  await pause();
  // Dilated, so the walls of the other levels are taken too
  const taken = dilate(otherFloors, w, h, closing);
  const filled = mask.slice();
  for (let i = 0; i < mask.length; i++) mask[i] &= 1 - taken[i];
  // Gives back the strip along the other floors, with the outer walls the levels then meet
  const allowance = Math.ceil(OUTER_WALL_ALLOWANCE / cellSize);
  const beside = dilate(mask, w, h, closing - allowance);
  const otherClosed = erode(taken, w, h, closing);
  for (let i = 0; i < mask.length; i++) {
    mask[i] |= beside[i] & filled[i] & (1 - otherClosed[i]);
  }
  keepScannedParts(mask, w, h, level, grid, pad);
  mask = dilate(mask, w, h, allowance);
  // Nothing outside the box is sent
  if (local.bounds) {
    for (let row = 0; row < h; row++) {
      for (let col = 0; col < w; col++) {
        const inside =
          row >= pad && row < rows + pad && col >= pad && col < cols + pad;
        if (!inside) mask[row * w + col] = 0;
      }
    }
  }

  return {
    cellSize,
    cols: w,
    rows: h,
    minX: minX - pad * cellSize,
    minZ: minZ - pad * cellSize,
    mask,
  };
}

/**
 * @param mask A binary grid
 * @param w Width of the grid
 * @param h Height of the grid
 * @param r Radius of the square kernel in cells
 * @param value 1 to dilate, 0 to erode
 * @returns the grid dilated or eroded with a square kernel, as two separable passes
 */
function morph(
  mask: Uint8Array,
  w: number,
  h: number,
  r: number,
  value: 0 | 1,
): Uint8Array {
  if (r <= 0) return mask;
  // A cell takes the value if any cell within r along the line has it: the nearest one before and after
  function pass(src: Uint8Array, horizontal: boolean): Uint8Array {
    const dst = new Uint8Array(src.length).fill(1 - value);
    // Cells of a line are `step` apart, lines start `lineStep` apart
    const [len, lines, step, lineStep] = horizontal
      ? [w, h, 1, w]
      : [h, w, w, 1];
    for (let line = 0; line < lines; line++) {
      const start = line * lineStep;
      let before = -Infinity;
      for (let i = 0, at = start; i < len; i++, at += step) {
        if (src[at] === value) before = i;
        if (i - before <= r) dst[at] = value;
      }
      let after = Infinity;
      for (let i = len - 1, at = start + i * step; i >= 0; i--, at -= step) {
        if (src[at] === value) after = i;
        if (after - i <= r) dst[at] = value;
      }
    }
    return dst;
  }
  return pass(pass(mask, true), false);
}

/**
 * @param mask A binary grid
 * @param w Width of the grid
 * @param h Height of the grid
 * @param r Radius in cells
 * @returns the dilated grid
 */
function dilate(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
  return morph(mask, w, h, r, 1);
}

/**
 * @param mask A binary grid
 * @param w Width of the grid
 * @param h Height of the grid
 * @param r Radius in cells
 * @returns the eroded grid
 */
function erode(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
  return morph(mask, w, h, r, 0);
}

/**
 * Fills, in place, the enclosed empty parts of the grid, if mostly covered when the cover is known
 *
 * @param mask A binary grid
 * @param w Width of the grid
 * @param h Height of the grid
 * @param covered The cells under a ceiling, undefined to fill all the enclosed parts
 */
function fillHoles(
  mask: Uint8Array,
  w: number,
  h: number,
  covered?: Uint8Array,
): void {
  const seen = new Uint8Array(mask.length);
  // Each empty cell is queued once, the cells of a part are queue[first..end)
  const queue = new Int32Array(mask.length);
  let end = 0;
  function visit(n: number): void {
    if (mask[n] || seen[n]) return;
    seen[n] = 1;
    queue[end++] = n;
  }
  for (let start = 0; start < mask.length; start++) {
    if (mask[start] || seen[start]) continue;
    const first = end;
    visit(start);
    let touchesBorder = false;
    let coveredCells = 0;
    for (let next = first; next < end; next++) {
      const i = queue[next];
      const x = i % w;
      const y = (i - x) / w;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) {
        touchesBorder = true;
      }
      coveredCells += covered?.[i] ?? 0;
      if (x > 0) visit(i - 1);
      if (x < w - 1) visit(i + 1);
      if (y > 0) visit(i - w);
      if (y < h - 1) visit(i + w);
    }
    const isIndoor =
      !covered || coveredCells >= MIN_COVERED_HOLE_SHARE * (end - first);
    if (!touchesBorder && isIndoor) {
      for (let next = first; next < end; next++) mask[queue[next]] = 1;
    }
  }
}

/**
 * Keeps, in place, the connected parts of the footprint around the scanners of the level,
 * dropping what is seen through the windows. Without scanners the small parts are dropped as noise.
 *
 * @param mask A binary grid
 * @param w Width of the grid
 * @param h Height of the grid
 * @param level The level
 * @param grid The unpadded grid
 * @param pad The padding of the mask
 */
function keepScannedParts(
  mask: Uint8Array,
  w: number,
  h: number,
  level: Level,
  grid: Grid,
  pad: number,
): void {
  const labels = new Int32Array(mask.length).fill(-1);
  const sizes: number[] = [];
  const stack = new Int32Array(mask.length);
  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || labels[start] >= 0) continue;
    const label = sizes.length;
    let size = 0;
    let top = 0;
    stack[top++] = start;
    labels[start] = label;
    while (top > 0) {
      const i = stack[--top];
      size++;
      const x = i % w;
      const y = Math.floor(i / w);
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
          const n = ny * w + nx;
          if (mask[n] && labels[n] < 0) {
            labels[n] = label;
            stack[top++] = n;
          }
        }
      }
    }
    sizes.push(size);
  }

  const kept = new Set<number>();
  if (level.scanners.length) {
    for (const [sx, sz] of level.scanners) {
      const col = Math.floor((sx - grid.minX) / grid.cellSize) + pad;
      const row = Math.floor((sz - grid.minZ) / grid.cellSize) + pad;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const x = col + dx;
          const y = row + dy;
          if (x >= 0 && x < w && y >= 0 && y < h && labels[y * w + x] >= 0) {
            kept.add(labels[y * w + x]);
          }
        }
      }
    }
  }
  // No scanner over a part, e.g. scanners of the fallback or above a stairwell
  if (!kept.size) {
    const minCells = MIN_PART_AREA / grid.cellSize ** 2;
    for (const [label, size] of sizes.entries()) {
      if (size >= minCells) kept.add(label);
    }
  }
  for (let i = 0; i < mask.length; i++) {
    if (mask[i] && !kept.has(labels[i])) mask[i] = 0;
  }
}

/**
 * @param mask A binary grid
 * @returns the number of set cells
 */
function countCells(mask: Uint8Array): number {
  let count = 0;
  for (const cell of mask) count += cell;
  return count;
}

/**
 * @param footprint A footprint
 * @returns the footprint cropped to its set cells
 */
function cropFootprint(footprint: FloorFootprint): FloorFootprint {
  const { cols, rows, mask, cellSize } = footprint;
  let [c0, c1, r0, r1] = [cols, -1, rows, -1];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!mask[r * cols + c]) continue;
      c0 = Math.min(c0, c);
      c1 = Math.max(c1, c);
      r0 = Math.min(r0, r);
      r1 = Math.max(r1, r);
    }
  }
  if (c1 < 0) return { ...footprint, cols: 0, rows: 0, mask: new Uint8Array() };
  const w = c1 - c0 + 1;
  const h = r1 - r0 + 1;
  const cropped = new Uint8Array(w * h);
  for (let r = 0; r < h; r++) {
    cropped.set(
      mask.subarray((r + r0) * cols + c0, (r + r0) * cols + c0 + w),
      r * w,
    );
  }
  return {
    cellSize,
    cols: w,
    rows: h,
    minX: footprint.minX + c0 * cellSize,
    minZ: footprint.minZ + r0 * cellSize,
    mask: cropped,
  };
}
